import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { apiPost } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2 } from "lucide-react";

export default function ApplyLeader() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    position: "",
    organization: "",
    organization_website: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiPost("/org-leader-applications", form);
      setDone(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center bg-background p-6 grain">
        <div className="max-w-md w-full bg-white border-2 border-ink rounded-3xl shadow-pop p-8 text-center animate-rise" data-testid="apply-leader-success">
          <div className="mx-auto h-16 w-16 rounded-full grid place-items-center border-2 border-ink mb-4" style={{ background: "#B8FF3B" }}>
            <CheckCircle2 size={30} />
          </div>
          <h2 className="text-2xl font-extrabold mb-2">Request submitted!</h2>
          <p className="text-muted-foreground mb-6">
            Thanks, {form.name.split(" ")[0] || "there"}. An admin will review your
            request to list <b>{form.organization}</b>. Once approved, you'll get an
            email inviting you to create your account with a password of your choice —
            you can't sign in until then.
          </p>
          <Link to="/login">
            <Button className="w-full h-12 rounded-full border-2 border-ink shadow-pop press font-bold" data-testid="back-to-login">
              Back to sign in
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background grain">
      <div className="mx-auto max-w-2xl px-4 py-10">
        <Link to="/login" className="inline-block mb-8"><Logo type="wordmark" variant="black" height={32} /></Link>
        <div className="bg-white border-2 border-ink rounded-3xl shadow-pop p-8 animate-rise">
          <span className="inline-block text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border-2 border-ink mb-4" style={{ background: "#31B8FF", color: "#0F1116" }}>
            Organization Leader
          </span>
          <h1 className="text-3xl font-extrabold mb-2">Don't see your organization?</h1>
          <p className="text-muted-foreground mb-7">
            Apply to have your organization listed and become its leader. We'll
            email you once an admin approves your request.
          </p>

          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="apply-leader-form">
            <div>
              <Label>Your name</Label>
              <Input value={form.name} onChange={set("name")} required data-testid="leader-name" className="mt-1 border-2 border-ink h-12 rounded-xl" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={set("email")} required data-testid="leader-email" className="mt-1 border-2 border-ink h-12 rounded-xl" />
            </div>
            <div>
              <Label>Position</Label>
              <Input value={form.position} onChange={set("position")} required data-testid="leader-position" className="mt-1 border-2 border-ink h-12 rounded-xl" placeholder="e.g. Youth Pastor" />
            </div>
            <div>
              <Label>Organization</Label>
              <Input value={form.organization} onChange={set("organization")} required data-testid="leader-organization" className="mt-1 border-2 border-ink h-12 rounded-xl" />
            </div>
            <div className="sm:col-span-2">
              <Label>Organization website</Label>
              <Input value={form.organization_website} onChange={set("organization_website")} data-testid="leader-website" className="mt-1 border-2 border-ink h-12 rounded-xl" placeholder="https://" />
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={busy} data-testid="leader-submit" className="w-full h-12 rounded-full border-2 border-ink shadow-pop press font-bold">
                {busy ? "Submitting…" : "Submit request"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
